/**
 * TOP-500 — resumen de lo que va a pasar ANTES de encolar.
 *
 * El servidor calcula el resumen en seco (`POST /enqueue` con `dry_run: true`) y esta
 * ventana lo muestra tal cual. Nada se decide acá: solo se ordena para que el usuario
 * entienda qué se va a encolar y qué no, y por qué.
 *
 * Tres cosas que NO se mezclan, por pedido explícito del diseño:
 *  - "No pasan los filtros" son productos que se miraron y no cumplen.
 *  - "Sin ficha de Amazon" son productos que NO se pudieron mirar: no es lo mismo, y
 *    quedan disponibles para la próxima.
 *  - "Sin producto claro" y "Empates por nombre" son productos con varios candidatos de
 *    Amazon posibles; se deciden a mano, no se encolan a ciegas.
 *
 * El desglose por motivo sale de `filtro`, que el servidor arma contando los motivos de
 * cada decisión con acción `filtro` (`enqueue-selection.ts`); un producto con dos
 * motivos suma en los dos, así que la suma del desglose puede superar el total.
 */
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Loader2 } from 'lucide-react';
import { SITE_FLAGS } from '../shared';

/** Resumen en seco que devuelve `POST /publisher/top500/enqueue`. */
export interface EnqueuePreview {
  total: number;
  encolar: number;
  ya_cargado: number;
  sin_asin: number;
  filtro: Record<string, number>;
  filtro_total: number;
  sin_datos: number;
  ruido: number;
  empate_nombre: number;
  familias: number;
  variantes_estimadas: number;
  /**
   * Productos que van a la cola arrastrando familia pero cuya familia todavía no se
   * consultó en Amazon. Se resuelven recién al confirmar, así que "Familias" y "Variantes
   * estimadas" son un piso: el total real puede ser mayor.
   */
  familias_sin_consultar: number;
  sites: string[];
  dry_run?: boolean;
}

/** Lo que devuelve el encolado de verdad (`dry_run: false`). */
export interface EnqueueCommitResult extends EnqueuePreview {
  queued: number;
  duplicates: number;
  family_enqueued: number;
  /** Keepa cortó por cuota durante el encolado: faltan variantes o fichas. */
  keepa_budget_stop?: boolean;
  family_capped: number;
  errors: string[];
}

interface Props {
  preview: EnqueuePreview | null;
  /** Se está pidiendo el resumen. */
  loading: boolean;
  /** Se está encolando de verdad. */
  confirming: boolean;
  error: string | null;
  onCancel: () => void;
  onConfirm: () => void;
}

function Fila({
  label,
  value,
  muted = false,
}: {
  label: string;
  value: ReactNode;
  muted?: boolean;
}) {
  return (
    <div className="flex items-center justify-between py-1.5 border-b border-[var(--color-border)] last:border-b-0">
      <span
        className={`text-xs ${muted ? 'text-[var(--color-text-tertiary)]' : 'text-[var(--color-text-secondary)]'}`}
      >
        {label}
      </span>
      <span
        className={`text-xs font-medium ${muted ? 'text-[var(--color-text-tertiary)]' : 'text-[var(--color-text)]'}`}
      >
        {value}
      </span>
    </div>
  );
}

export function EnqueueSummaryModal({
  preview,
  loading,
  confirming,
  error,
  onCancel,
  onConfirm,
}: Props) {
  const { t } = useTranslation();
  const motivos = Object.entries(preview?.filtro ?? {}).sort((a, b) => b[1] - a[1]);

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onCancel}
    >
      <div
        className="bg-[var(--color-bg)] rounded-xl border border-[var(--color-border)] w-full max-w-md max-h-[80vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-[var(--color-border)]">
          <h3 className="font-bold text-[var(--color-text)]">{t('pub.top500.preview')}</h3>
          <button
            onClick={onCancel}
            aria-label={t('pub.top500.closeLabel')}
            className="p-1 rounded hover:bg-[var(--color-bg-tertiary)]"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-4 space-y-3">
          {loading && (
            <div className="flex items-center gap-2 py-6 text-xs text-[var(--color-text-secondary)]">
              <Loader2 size={14} className="animate-spin" />
              {t('common.loading')}
            </div>
          )}

          {error && !loading && <p className="text-xs text-[var(--color-error)]">{error}</p>}

          {preview && !loading && (
            <>
              <div>
                <Fila label={t('pub.top500.summary.total')} value={preview.total} />
                <Fila label={t('pub.top500.summary.encolar')} value={preview.encolar} />
                <Fila label={t('pub.top500.summary.yaCargado')} value={preview.ya_cargado} muted />
                <Fila label={t('pub.top500.summary.sinAsin')} value={preview.sin_asin} muted />
                <Fila label={t('pub.top500.summary.filtro')} value={preview.filtro_total} muted />
                {motivos.map(([motivo, n]) => (
                  <Fila
                    key={motivo}
                    label={`   • ${t(`pub.top500.reasons.${motivo}`, motivo)}`}
                    value={n}
                    muted
                  />
                ))}
                <Fila label={t('pub.top500.summary.sinDatos')} value={preview.sin_datos} muted />
                <Fila label={t('pub.top500.summary.ruido')} value={preview.ruido} muted />
                <Fila
                  label={t('pub.top500.summary.empateNombre')}
                  value={preview.empate_nombre}
                  muted
                />
                <Fila label={t('pub.top500.summary.familias')} value={preview.familias} />
                <Fila
                  label={t('pub.top500.summary.variantes')}
                  value={preview.variantes_estimadas}
                />
                {/* Sólo aparece cuando hay familias sin mirar: si no, no hay nada que aclarar. */}
                {preview.familias_sin_consultar > 0 && (
                  <Fila
                    label={t('pub.top500.summary.familiasSinConsultar')}
                    value={preview.familias_sin_consultar}
                    muted
                  />
                )}
                <Fila
                  label={t('pub.top500.summary.sites')}
                  value={
                    preview.sites.length > 0
                      ? preview.sites.map((s) => `${SITE_FLAGS[s] || ''} ${s}`).join('  ')
                      : '—'
                  }
                />
              </div>

              {preview.encolar === 0 && (
                <p className="text-xs text-[var(--color-text-secondary)]">
                  {t('pub.top500.nothingToQueue')}
                </p>
              )}
            </>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 p-4 border-t border-[var(--color-border)]">
          <button
            onClick={onCancel}
            disabled={confirming}
            className="px-3 py-1.5 text-xs rounded border border-[var(--color-border)] text-[var(--color-text-secondary)] hover:text-[var(--color-text)] disabled:opacity-50"
          >
            {t('pub.top500.cancel')}
          </button>
          <button
            onClick={onConfirm}
            disabled={loading || confirming || !preview || preview.encolar === 0}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded bg-[var(--color-primary)] text-white disabled:opacity-50"
          >
            {confirming && <Loader2 size={12} className="animate-spin" />}
            {t('pub.top500.confirm')}
          </button>
        </div>
      </div>
    </div>
  );
}
